import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { isCallbackUrl } from '@services/tab-aware-auth';

/**
 * Проверка роли `access` остаётся здесь: без неё пользователь, у которого роль есть не в том client,
 * увидит пустые разделы вместо отказа.
 *
 * Порядок проверок важен:
 * 1) восстановление адреса возврата — на callback-URL после успешного входа сохранённый раздел
 *    ещё лежит в sessionStorage, а адрес уже корневой;
 * 2) callback Keycloak пропускаем: `checkAuthOnStart()` разбирает его в инициализаторе приложения,
 *    иначе authorize ушёл бы по кругу;
 * 3) нет сессии — уходим на Keycloak с этим же адресом, чтобы вернуться в тот же раздел;
 * 4) сессия есть, а токена нет (стёрли localStorage, выход в другой вкладке ещё не долетел) —
 *    это тоже «нет сессии», а не «нет прав»: гасим состояние и уходим на вход, а не на портал.
 *
 * checkAuthOnStart() отрабатывает до первой навигации, значение isAuthenticated$ здесь актуальное.
 */
export const authGuard: CanActivateFn = (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const url = state?.url || '/';

    return auth.isAuthenticated$.pipe(
        take(1),
        map((isAuthenticated) => {
            if (!isAuthenticated) {
                if (isCallbackUrl(url)) {
                    return true;
                }

                auth.loginSso(url);
                return false;
            }

            const returnUrl = auth.takeReturnUrl();
            if (returnUrl && returnUrl !== url) {
                return router.parseUrl(returnUrl);
            }

            const token = auth.getAccessTokenNow();
            if (!token) {
                auth.logoffLocal();
                auth.loginSso(url);
                return false;
            }

            if (!auth.hasSpaAccess(token)) {
                auth.handleAccessDenied();
                return false;
            }

            return true;
        }),
    );
};
